import React, { useState, useEffect } from 'react';
import { FiUsers, FiEdit, FiTrash2, FiSearch, FiPlus, FiKey } from 'react-icons/fi';
import userService from '../services/userService';
import UserFormModal from '../components/AuthComponents/UserFormModal';
import '../styles/UserManagement.css';

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isPasswordChange, setIsPasswordChange] = useState(false); // New state

  const handleChangePassword = (user) => {
    setSelectedUser(user);
    setIsPasswordChange(true); // Set to true for password change
    setIsModalOpen(true);
  };
  const [loading, setLoading] = useState(true); // Add loading state

  const loggedInUserEmail = localStorage.getItem('email');
  const loggedInUserRole = localStorage.getItem('role');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true); // Set loading to true before fetching
    try {
      const response = await userService.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false); // Set loading to false after fetching (success or error)
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await userService.deleteUser(userId);
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleSave = async (userData) => {
    try {
      if (userData._id) {
        await userService.updateUser(userData._id, userData);
      } else {
        await userService.createUser(userData);
      }
      fetchUsers();
      setIsModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error saving user:', error);
    }
  };

  const handlePasswordSave = async (userId, newPassword) => {
    try {
      await userService.updatePassword(userId, newPassword);
      setIsModalOpen(false);
      setIsPasswordChange(false);
      setSelectedUser(null);
      alert('Password updated successfully!'); // Provide feedback
    } catch (error) {
      console.error('Error updating password:', error);
      alert('Failed to update password. Please try again.'); // Provide feedback
    }
  };

  const handleAddNew = () => {
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="user-management-page">
      <div className="user-management-container">
        <header className="user-management-header">
          <div className="header-icon">
            <FiUsers size={40} />
          </div>
          <h1 className="user-management-title">Manage Users</h1>
          <p className="user-management-subtitle">
            View, edit, and manage user accounts with ease.
          </p>
        </header>

        <div className="toolbar">
          <div className="search-bar">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="add-user-btn" onClick={handleAddNew}>
            <FiPlus />
            <span>Add New User</span>
          </button>
        </div>

        <div className="user-table-container">
          {loading ? (
            <div className="loading-spinner-container">
              <div className="loading-spinner"></div>
              <p>Loading users...</p>
            </div>
          ) : (
            <table className="user-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Email</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map(user => (
                    <tr key={user._id}>
                      <td>
                        <div className="user-info">
                          <div className="user-details">
                            <span className="user-id">ID: {user._id}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`role-badge role-${user.role.toLowerCase()}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>
                        <a href={`mailto:${user.email}`} className="user-email">{user.email}</a>
                      </td>
                      <td className="actions-cell">
                        {user.email === loggedInUserEmail ? (
                          <>
                            <button onClick={() => handleEdit(user)} className="action-btn edit-btn">
                              <FiEdit />
                              <span>Edit</span>
                            </button>
                            <button onClick={() => handleChangePassword(user)} className="action-btn edit-btn"> {/* Reusing edit-btn style for now */}
                              <FiKey /> {/* Using a key icon for password */}
                              <span>Change Password</span>
                            </button>
                            <button className="action-btn delete-btn disabled-btn" disabled title="Cannot delete your own account">
                              <FiTrash2 />
                              <span>Delete</span>
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => handleEdit(user)} className="action-btn edit-btn">
                              <FiEdit />
                              <span>Edit</span>
                            </button>
                            <button onClick={() => handleDelete(user._id)} className="action-btn delete-btn">
                              <FiTrash2 />
                              <span>Delete</span>
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="no-users-message">
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <UserFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setIsPasswordChange(false); // Reset when closing
        }}
        onSave={handleSave}
        onPasswordSave={handlePasswordSave} // New prop
        user={selectedUser}
        isPasswordChange={isPasswordChange} // New prop
      />
    </div>
  );
};

export default UserManagementPage;
